import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { Box, Slide, useScrollTrigger } from "@material-ui/core";
import { useDeviceTypes } from "halifax";
import { RouteComponentProps } from "react-router-dom";
import { B2B_PORTAL_BANNER_CONTAINER_ID, RewardsAccount } from "redmond";

import {
  HIDDEN_BANNER_UNPROTECTED_PATHS,
  HIDDEN_HEADER_PATHS_DESKTOP,
  isCoreHomepageBookingConfirmationPath,
  pathsListContainsPath,
  PRICE_MATCH_BANNER_PATHS,
} from "../../../utils/urlPaths";
import { isPremiumStaysFunnelPath } from "../../../utils/homepageEntryPaths";
import "./styles.scss";
import { CapOneHeaderHomePageRightSection } from "../Header/components/DesktopHeader";

export interface IRootBannerProps extends RouteComponentProps {
  headerDisplayed?: boolean;
  isHidden?: boolean;
  canViewConsumer: boolean;
  currentAccount: RewardsAccount | undefined;
  hideOnScroll?: boolean;
  shouldShowCoreHomepageHeader?: boolean;
  isRewardsDisabled?: boolean;
}

const HideOnScroll = ({ children }: { children: JSX.Element }) => {
  const trigger = useScrollTrigger({ disableHysteresis: true });

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
};

export const RootBanner = (props: IRootBannerProps) => {
  const { isHidden, isRewardsDisabled, ...browserRouterProps } = props;
  const { history } = browserRouterProps;
  const { matchesMobile } = useDeviceTypes();
  const [renderRightNav, setRenderRightNav] = useState(true);

  const showCoreHomepageForSinglePortal =
    props.shouldShowCoreHomepageHeader ?? false;

  useEffect(() => {
    if (!matchesMobile) {
      const { pathname } = history.location;
      const isInHiddenPaths = pathsListContainsPath(
        HIDDEN_HEADER_PATHS_DESKTOP,
        pathname
      );

      const isConfirmationPage = pathname.includes("/confirm/");
      const isOnHotelsFunnel =
        /^\/hotels\/[^/]+/.test(pathname) && !isConfirmationPage;
      const isOnStaysFunnel =
        /^\/stays\/[^/]+/.test(pathname) && !isConfirmationPage;
      const isOnPremiumStaysFunnel = isPremiumStaysFunnelPath(pathname);
      const isCoreHomepageBookingConfirmation =
        showCoreHomepageForSinglePortal &&
        isCoreHomepageBookingConfirmationPath(pathname);

      if (
        isInHiddenPaths ||
        isOnHotelsFunnel ||
        isOnStaysFunnel ||
        isOnPremiumStaysFunnel ||
        isCoreHomepageBookingConfirmation
      ) {
        setRenderRightNav(false);
      } else {
        setRenderRightNav(true);
      }
    }
  }, [
    history.location.pathname,
    matchesMobile,
    showCoreHomepageForSinglePortal,
  ]);

  const content = (
    <Box
      className={clsx(
        "b2b-portal-banner-root",
        "corp",
        "home-screen-redesign",
        {
          "includes-right-section": renderRightNav && !matchesMobile,
          mobile: matchesMobile,
          "checkout-banner": PRICE_MATCH_BANNER_PATHS.includes(
            history.location.pathname
          ),
          isHidden,
          "rewards-disabled": isRewardsDisabled,
        }
      )}
    >
      <div
        id={B2B_PORTAL_BANNER_CONTAINER_ID}
        className={clsx({
          mobile: matchesMobile,
          isHidden: HIDDEN_BANNER_UNPROTECTED_PATHS.includes(
            history.location.pathname
          ),
        })}
      />

      {renderRightNav && !matchesMobile && (
        <CapOneHeaderHomePageRightSection displayOnlyIcon {...props} />
      )}
    </Box>
  );

  return props.hideOnScroll ? <HideOnScroll>{content}</HideOnScroll> : content;
};
